import * as ChapterType from "utils/ChapterType";
import ChapterProgressTextContainer from "components/ChapterProgressText";
import classnames from "classnames";
import CSSTransitionGroup from "react-addons-css-transition-group";
import PropTypes from "prop-types";
import React from "react";
import CaptionDialog from "containers/captions/CaptionDialogContainer";
import ToggleGalleryViewButton from "containers/ToggleGalleryViewButton";
import { autobind } from "core-decorators";
import { decorate as mixin } from "react-mixin";
import { AnalyticsActions, AnalyticsMixin } from "utils/Analytics";
import tippy from "tippy.js";
import { keyboardEventPassthroughHandler } from "utils/keyboardEventPassthroughHandler";
import styles from "./Navigation.css";
import StoryTitleMonogramBlock from "./StoryTitleMonogramBlock";
import ChapterList from "./ChapterList";

function copyText(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style = { position: "absolute", left: "-9999px" };
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

@mixin(AnalyticsMixin)
export default class Navigation extends React.PureComponent {
  static displayName = "Navigation";

  static propTypes = {
    chapterListVisible: PropTypes.bool.isRequired,
    currentChapter: PropTypes.object,
    forcingChapterCover: PropTypes.bool.isRequired,
    isShowingSlideshowCaptions: PropTypes.bool,
    onHomeClick: PropTypes.func.isRequired,
    onModalClose: PropTypes.func.isRequired,
    onModalOpen: PropTypes.func.isRequired,
    onReportActivity: PropTypes.func.isRequired,
    routePrefix: PropTypes.string.isRequired,
    router: PropTypes.object.isRequired,
    setChapterListVisible: PropTypes.func.isRequired,
    shareURL: PropTypes.string.isRequired,
    showBackButton: PropTypes.bool.isRequired,
    showChapterProgress: PropTypes.bool.isRequired,
    showHamburger: PropTypes.bool.isRequired,
    showShareButton: PropTypes.bool.isRequired,
    showSlideshowControls: PropTypes.bool,
    showStoryTitle: PropTypes.bool,
    showingStoryCover: PropTypes.bool.isRequired,
    stateKey: PropTypes.string.isRequired,
    storySlug: PropTypes.string.isRequired,
    useFlattenedInterviewQuestionsList: PropTypes.bool.isRequired,
    useSwipeableInterview: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    isShowingSlideshowCaptions: false,
    showSlideshowControls: false,
  };

  static analyticsActions = [
    {
      action: AnalyticsActions.timelineCaptionClick,
      handler: "handleSlideshowCaptionClick",
    },
    {
      action: AnalyticsActions.timelineShowClick,
      handler: "handleSlideshowIndexShow",
    },
    {
      action: AnalyticsActions.timelineHideClick,
      handler: "handleSlideshowIndexHide",
    },
    {
      action: AnalyticsActions.chapterMenuViewed,
      handler: "handleChapterListOpen",
    },
    {
      action: AnalyticsActions.chapterMenuClosed,
      handler: "handleChapterListClose",
    },
  ];

  UNSAFE_componentWillUpdate(nextProps) {
    if (this.props.router !== nextProps.router) {
      this.props.setChapterListVisible(false);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.textCopiedTippyHideTimeout);
  }

  @autobind
  handleChapterListOpen() {
    this.props.setChapterListVisible(true);
    this.props.onModalOpen();
  }

  @autobind
  handleChapterListClose(options) {
    this.props.setChapterListVisible(false);
    this.props.onModalClose(options);
  }

  handleSlideshowCaptionClick() {
    //noop
  }

  handleSlideshowIndexHide() {
    //noop
  }

  handleSlideshowIndexShow() {
    //noop
  }

  /**
   * Timeout stored when we're showing the text copied tippy
   */
  textCopiedTippyInstance;

  textCopiedTippyHideTimeout;

  handleShareClick = async (event) => {
    const target = event.target;
    const shareOptions = {
      url: this.props.shareURL,
    };

    if (navigator.canShare?.(shareOptions)) {
      try {
        await navigator.share(shareOptions);
      } catch {}
    } else {
      copyText(this.props.shareURL);

      const textCopiedTippyInstance = tippy(
        target.querySelector("[class*=icon-]"),
        {
          content: "Link Copied!",
          theme: "share-tooltip",
        },
      );
      this.textCopiedTippyInstance = textCopiedTippyInstance;

      textCopiedTippyInstance.show();

      if (event.type === "keyup") {
        // Keep the focus on the button

        this.textCopiedTippyHideTimeout = setTimeout(() => {
          textCopiedTippyInstance.hide();

          this.textCopiedTippyHideTimeout = null;

          target.focus();
        }, 5000);
      }
    }
  };

  get isShowingCover() {
    return this.props.showingStoryCover || this.props.forcingChapterCover;
  }

  get isLeftSideSmallQAndA() {
    ///////
    // For left side small Explainer/Q&A menus that are not in swipeable mode,
    // the navigation icons should not overlap the top question.
    // Use this property to set a CSS flag for relevant styling.
    //////
    const currentChapter = this.props.currentChapter;
    if (!currentChapter) return false;
    return (
      currentChapter.type === ChapterType.INTERVIEW &&
      currentChapter.menuLocation === "left" &&
      !this.props.useSwipeableInterview &&
      this.props.useFlattenedInterviewQuestionsList
    );
  }

  get canDisplayChapterProgress() {
    //Use the chapter progress in the nav bar if the side chapter menu is showing.
    return !!this.props.currentChapter && this.props.showChapterProgress;
  }

  renderChapterProgressText() {
    if (this.canDisplayChapterProgress) {
      return (
        <div className="nav__chapter-progress">
          <ChapterProgressTextContainer
            chapterId={this.props.currentChapter.id}
          />
        </div>
      );
    }
  }

  renderChapterList() {
    if (this.props.chapterListVisible) {
      return <ChapterList onClose={this.handleChapterListClose} />;
    }
    return null;
  }

  renderStoryTitleBlock() {
    return (
      <StoryTitleMonogramBlock showBackButton={this.props.showBackButton} />
    );
  }

  render() {
    return (
      <div
        aria-label="Player Navigation"
        className={styles.root}
        onTouchStart={(e) => {
          e.stopPropagation();
          this.props.onReportActivity();
        }}
        role="navigation"
      >
        <nav
          className={classnames(styles.nav, {
            "nav--showing-chapter-cover": this.props.forcingChapterCover,
            "nav--showing-chapter-progress": this.props.showChapterProgress,
            "nav--showing-story-cover": this.props.showingStoryCover,
            "nav--narrow-left-side-icons": this.isLeftSideSmallQAndA,
          })}
          key="nav"
          role="navigation"
        >
          <div className={styles.background} />
          {this.renderStoryTitleBlock()}
          <div className="nav__right-container">
            {this.props.showSlideshowControls && (
              <ToggleGalleryViewButton
                onClose={this.handleSlideshowIndexHide}
                onOpen={this.handleSlideshowIndexShow}
                stateKey={this.props.stateKey}
              />
            )}
            {!this.props.showShareButton || window.offline ? null : (
              <div
                className="nav__share font-size--icon--large"
                onClick={this.handleShareClick}
                onMouseLeave={(mouseEvent) => {
                  if (!this.textCopiedTippyInstance) return;
                  this.textCopiedTippyInstance.hideWithInteractivity(
                    mouseEvent,
                  );
                }}
                onBlur={() => {
                  this.textCopiedTippyInstance?.hide();
                }}
                role="button"
                tabIndex={0}
                onKeyUp={keyboardEventPassthroughHandler(this.handleShareClick)}
                aria-label="share"
              >
                <span className="icon-Share2" />
              </div>
            )}
            {this.props.showHamburger && (
              <div
                aria-label="Chapter Menu"
                className="nav__hamburger font-size--icon--large"
                onClick={this.handleChapterListOpen}
                role="button"
                tabIndex={0}
                onKeyUp={keyboardEventPassthroughHandler(
                  this.handleChapterListOpen,
                )}
              >
                <span className="icon-More" />
              </div>
            )}
          </div>
        </nav>
        <CSSTransitionGroup
          transitionEnterTimeout={300}
          transitionLeaveTimeout={300}
          transitionName="chapter-listing"
        >
          {this.renderChapterList()}
        </CSSTransitionGroup>
        <CaptionDialog />
      </div>
    );
  }
}
